import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useLogout, useMe } from '../hooks/useAuth.js';
import { BottomTabBar } from './BottomTabBar.js';
import { CITY_LIST } from '../lib/cities.js';
import { useUnreadCount } from '../hooks/useMessages.js';

export function Layout() {
  const { data } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const user = data?.user;
  const unread = useUnreadCount();
  const unreadCount = unread.data?.count ?? 0;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-navy-50 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-[4.5rem] sm:h-[5.5rem] flex items-center justify-between gap-3">
          <Link to="/" aria-label="TowShare home" className="flex items-center min-w-0">
            <img
              src="/TowShareLongLogo.png"
              alt="TowShare.ca — Your trailer, on demand."
              className="h-11 sm:h-14 w-auto"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/search">Find a trailer</NavItem>
            <NavItem to="/list-your-trailer">List your trailer</NavItem>
            <NavItem to="/guarantee">$1,500 Guarantee</NavItem>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                {user.roles.includes('admin') && (
                  <NavLink
                    to="/admin"
                    className="text-sm font-medium px-2 sm:px-3 py-2 text-orange hover:text-orange-600"
                  >
                    Admin
                  </NavLink>
                )}
                <NavLink
                  to="/inbox"
                  className={({ isActive }) =>
                    `hidden lg:inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 ${
                      isActive ? 'text-orange' : 'text-navy-700 hover:text-navy'
                    }`
                  }
                >
                  Inbox
                  {unreadCount > 0 && <UnreadDot count={unreadCount} />}
                </NavLink>
                <NavLink
                  to="/account"
                  className="hidden lg:inline-block text-sm font-medium text-navy hover:text-navy-700 px-3 py-2 max-w-[12rem] truncate"
                >
                  {user.displayName ?? user.email}
                </NavLink>
                <button
                  className="hidden lg:inline-block btn-ghost text-sm"
                  onClick={async () => {
                    await logout.mutateAsync();
                    navigate('/');
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link to="/login" className="btn-primary text-sm">
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 pb-16 lg:pb-0">
        <Outlet />
      </main>
      <Footer />
      <BottomTabBar />
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-navy-50 bg-navy-50/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
        <div className="sm:col-span-2 lg:col-span-1">
          <img
            src="/TowShareLongLogo.png"
            alt="TowShare.ca"
            className="h-11 w-auto mb-3"
          />
          <p className="text-navy-600 text-sm max-w-xs">
            Canada's peer-to-peer trailer marketplace. Owners earn on idle trailers;
            renters skip the rental yard.
          </p>
        </div>

        <div className="text-sm text-navy-700">
          <h4 className="font-semibold mb-3 text-navy">Marketplace</h4>
          <ul className="space-y-2">
            <li>
              <Link to="/search" className="hover:text-navy">
                Find a trailer
              </Link>
            </li>
            <li>
              <Link to="/list-your-trailer" className="hover:text-navy">
                List your trailer
              </Link>
            </li>
            <li>
              <Link to="/how-it-works" className="hover:text-navy">
                How it works
              </Link>
            </li>
          </ul>
        </div>

        <div className="text-sm text-navy-700">
          <h4 className="font-semibold mb-3 text-navy">Trust &amp; safety</h4>
          <ul className="space-y-2">
            <li>
              <Link to="/guarantee" className="hover:text-navy">
                $1,500 Trailer Guarantee
              </Link>
            </li>
            <li>
              <Link to="/insurance" className="hover:text-navy">
                Insurance &amp; coverage
              </Link>
            </li>
            <li>
              <Link to="/id-verification" className="hover:text-navy">
                ID verification
              </Link>
            </li>
            <li>
              <Link to="/account/verify" className="hover:text-navy">
                Verify your ID
              </Link>
            </li>
          </ul>
        </div>

        <div className="text-sm text-navy-700">
          <h4 className="font-semibold mb-3 text-navy">Cities</h4>
          <ul className="space-y-2">
            {CITY_LIST.map((c) => (
              <li key={c.slug}>
                <Link to={`/trailer-rental/${c.slug}`} className="hover:text-navy">
                  Trailer rental {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="text-sm text-navy-700">
          <h4 className="font-semibold mb-3 text-navy">Company</h4>
          <ul className="space-y-2">
            <li>
              <Link to="/about" className="hover:text-navy">
                About
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-navy">
                Contact
              </Link>
            </li>
            <li>
              <Link to="/terms" className="hover:text-navy">
                Terms &amp; Conditions
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="hover:text-navy">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-navy-100 py-5 px-4">
        <div className="mx-auto max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-navy-500">
          <div className="flex items-center gap-1">
            <span>© {new Date().getFullYear()} TowShare.ca · A</span>
            <a
              href="https://waypointlabz.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-navy hover:text-orange"
            >
              Waypoint Labs
            </a>
            <span>company</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Payments secured by Stripe</span>
            <span>·</span>
            <span>Identity by Stripe</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function UnreadDot({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `text-sm font-medium px-3 py-2 rounded-md transition-colors ${
          isActive ? 'text-orange' : 'text-navy-700 hover:text-navy'
        }`
      }
    >
      {children}
    </NavLink>
  );
}
